import React, { useState, useEffect } from 'react';
import {
    Plus, Search, Edit, Trash2, Send, Clock, Bell,
    AlertTriangle, Pin, Eye, X, Paperclip, ExternalLink,
    Calendar, Users, UserCheck, FileText, BarChart2, Sparkles, BookOpen
} from 'lucide-react';
import NoticeCreationModal from '../components/Notices/NoticeCreationModal';
import NoticeStatsModal from '../components/Notices/NoticeStatsModal';
import AINoticeGenerator from '../components/Notices/AINoticeGenerator';
import FileViewer from '../components/Tasks/FileViewer';
import api from '../utils/api';
import NoticesAnalyticsDrawer from '../components/Analytics/NoticesAnalyticsDrawer';

const getEffectiveStatus = (notice) => {
    if (!notice || !notice.status) return 'draft';

    const { status, scheduled_date, expiry_date } = notice;

    // Check if expired
    if (expiry_date && new Date(expiry_date) < new Date()) {
        return 'expired';
    }

    // Published notices
    if (status === 'published') return 'published';

    // Scheduled notices
    if (status === 'scheduled') {
        if (scheduled_date) {
            const scheduledTime = new Date(scheduled_date).getTime();
            const now = Date.now();
            if (scheduledTime <= now) return 'published';
            return 'scheduled';
        }
        return 'published';
    }

    return 'draft';
};



const AdminNoticesPage = () => {
    const [activeTab, setActiveTab] = useState('onboarding');
    const [notices, setNotices] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    // Stats Modal State
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const [statsNotice, setStatsNotice] = useState(null);

    // File Viewing State
    const [viewingNoticeAttachments, setViewingNoticeAttachments] = useState(null);

    // AI Generator State
    const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false);
    const [aiEditNotice, setAiEditNotice] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    // Page Level Analytics State
    const [isPageAnalyticsOpen, setIsPageAnalyticsOpen] = useState(false);

    useEffect(() => {
        fetchNotices();
    }, [filter, activeTab]);

    const fetchNotices = async () => {
        setLoading(true);
        try {
            const params = { phase: activeTab };
            if (filter !== 'all') {
                params.status = filter;
            }

            const response = await api.get('/notices', { params });
            setNotices(response.data.notices || []);
        } catch (error) {
            console.error('Error fetching notices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNotice = () => {
        setSelectedNotice(null);
        setIsModalOpen(true);
    };

    const handleEditNotice = (notice) => {
        // If notice was AI generated, open AI generator for editing
        if (notice.is_ai_generated) {
            setAiEditNotice(notice);
            setIsAIGeneratorOpen(true);
        } else {
            setSelectedNotice(notice);
            setIsModalOpen(true);
        }
    };

    const handleOpenAIGenerator = () => {
        setAiEditNotice(null);
        localStorage.removeItem('ai_notice_draft_session');
        setIsAIGeneratorOpen(true);
    };

    const handleDeleteNotice = async (noticeId) => {
        if (!window.confirm('Are you sure you want to delete this notice?')) return;

        try {
            await api.delete(`/notices/${noticeId}`);
            fetchNotices();
        } catch (error) {
            console.error('Error deleting notice:', error);
            alert('Failed to delete notice');
        }
    };

    const handlePublishNotice = async (noticeId) => {
        try {
            await api.post(`/notices/${noticeId}/publish`);
            fetchNotices();
            alert('Notice published successfully!');
        } catch (error) {
            console.error('Error publishing notice:', error);
            alert('Failed to publish notice');
        }
    };

    const handleViewStats = (notice) => {
        setStatsNotice(notice);
        setStatsModalOpen(true);
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent':
                return <AlertTriangle className="w-4 h-4 text-red-500" />;
            case 'pinned':
                return <Pin className="w-4 h-4 text-amber-500" />;
            default:
                return <Bell className="w-4 h-4 text-primary" />;
        }
    };



    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const filteredNotices = notices
        .filter(notice => {
            if (filter === 'reminder') return notice.is_reminder;
            return true;
        })
        .filter(notice =>
            notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            notice.content?.toLowerCase().includes(searchQuery.toLowerCase())
        );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground mb-2">Activity Centre — Notices</h1>
                <p className="text-sm text-muted-foreground">Create and manage notices for employees</p>
            </div>

            {/* Tabs */}
            <div className="mb-6 border-b border-border">
                <nav className="flex gap-8">
                    {[
                        { key: 'onboarding', label: 'Onboarding Notices' },
                        { key: 'preboarding', label: 'Preboarding Notices' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => { setActiveTab(tab.key); setFilter('all'); setSearchQuery(''); }}
                            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.key
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Filter Bar */}
            <div className="bg-card border border-border rounded-lg p-3 mb-5">
                <div className="flex flex-wrap gap-3 items-center justify-between">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search notices..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-3 h-9 border border-border rounded-md text-sm bg-transparent outline-none focus:border-primary transition-colors"
                        />
                    </div>
                    <div className="flex gap-2.5 items-center flex-wrap">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="h-9 border border-border rounded-md px-3 text-sm bg-background outline-none focus:border-primary transition-colors"
                        >
                            <option value="all">All Notices</option>
                            <option value="draft">Drafts</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="published">Published</option>
                            <option value="reminder">Reminders</option>
                        </select>
                        <button
                            onClick={() => setIsPageAnalyticsOpen(true)}
                            className="flex items-center gap-2 bg-background text-indigo-600 border border-indigo-200 hover:bg-indigo-50 shadow-sm h-9 px-3.5 rounded-md text-sm font-semibold transition-colors"
                        >
                            <BarChart2 className="w-3.5 h-3.5" />
                            View Insights
                        </button>
                        <button
                            onClick={handleOpenAIGenerator}
                            className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground h-9 px-3.5 rounded-md text-sm font-semibold transition-colors shadow-sm"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create Notice
                        </button>
                    </div>
                </div>
            </div>

            {/* Notices Card List */}
            <div className="mb-6 space-y-3">
                {loading ? (
                    <div className="p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-muted-foreground text-sm">Loading notices...</p>
                    </div>
                ) : filteredNotices.length === 0 ? (
                    <div className="p-12 text-center bg-card border border-border rounded-lg">
                        <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <h3 className="text-base font-semibold text-foreground mb-2">No notices found</h3>
                        <p className="text-sm text-muted-foreground mb-4">Get started by creating your first {activeTab} notice</p>
                        <button
                            onClick={handleOpenAIGenerator}
                            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-5 py-2 rounded-md font-semibold text-sm hover:bg-primary/90 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create Notice
                        </button>
                    </div>
                ) : (
                    filteredNotices.map((notice) => {
                        const effectiveStatus = getEffectiveStatus(notice);
                        const statusStyles = {
                            draft: 'bg-muted text-muted-foreground',
                            scheduled: 'bg-amber-100 text-amber-700',
                            published: 'bg-green-100 text-green-700',
                            expired: 'bg-red-100 text-red-700'
                        };
                        const statusClass = statusStyles[effectiveStatus] || statusStyles.draft;

                        const priorityStyles = {
                            urgent: 'bg-red-100 text-red-700',
                            pinned: 'bg-amber-100 text-amber-700',
                            normal: 'bg-blue-100 text-blue-700'
                        };
                        const priorityClass = priorityStyles[notice.priority] || priorityStyles.normal;

                        return (
                            <div
                                key={notice.id}
                                className="bg-card border border-border rounded-lg p-4 transition-all hover:border-primary hover:shadow-sm"
                            >
                                {/* Row 1: Icon + Title + Badges */}
                                <div className="flex items-start gap-3 mb-2.5">
                                    <div className="mt-0.5 flex-shrink-0">
                                        {getPriorityIcon(notice.priority)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-foreground">{notice.title}</span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${priorityClass}`}>
                                                {notice.priority?.charAt(0).toUpperCase() + notice.priority?.slice(1)}
                                            </span>
                                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusClass}`}>
                                                {effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}
                                            </span>
                                            {notice.show_popup_on_login && (
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-purple-100 text-purple-700">Popup</span>
                                            )}
                                            {notice.is_reminder && (
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 flex items-center gap-0.5">
                                                    <Bell className="w-2.5 h-2.5" /> Reminder
                                                </span>
                                            )}
                                            {notice.attachments && notice.attachments.length > 0 && (
                                                <button
                                                    onClick={() => setViewingNoticeAttachments(notice)}
                                                    className="inline-flex items-center gap-1 text-[11px] text-orange-600 bg-orange-50 hover:bg-orange-100 px-2 py-0.5 rounded-md transition-colors"
                                                >
                                                    <Paperclip className="w-2.5 h-2.5" /> {notice.attachments.length}
                                                </button>
                                            )}
                                            {notice.external_link && (
                                                <a
                                                    href={notice.external_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary hover:underline"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </div>
                                        {notice.created_via_course_id && (
                                            <div className="inline-flex items-center gap-1 mt-1 text-[11px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                                <BookOpen className="w-2.5 h-2.5" /> Course: {notice.course_name}
                                            </div>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {notice.content?.replace(/<[^>]*>/g, '').substring(0, 120) || 'No content'}
                                        </p>
                                    </div>
                                </div>

                                {/* Row 2: Metadata */}
                                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2.5 pl-7 flex-wrap">
                                    <span className="flex items-center gap-1">
                                        {notice.assignment_type === 'all' ? <><Users className="w-3 h-3" /> Company-wide</> :
                                            notice.assignment_type === 'role' ? <><UserCheck className="w-3 h-3" /> {notice.assigned_roles?.length || 0} role(s)</> :
                                                <><UserCheck className="w-3 h-3" /> {notice.assigned_employee_ids?.length || 0} employee(s)</>}
                                    </span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <Eye className="w-3 h-3" /> {notice.view_count || 0} / {notice.target_count || 0} views
                                    </span>
                                    <span className="text-border">•</span>
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {notice.expiry_date ? (
                                            <span className={new Date(notice.expiry_date) < new Date() ? 'text-destructive' : ''}>{formatDate(notice.expiry_date)}</span>
                                        ) : 'No expiry'}
                                    </span>
                                </div>

                                {/* Row 3: Actions */}
                                <div className="flex items-center justify-end gap-2 pl-7 flex-wrap">
                                    {notice.status === 'draft' && (
                                        <button
                                            onClick={() => handlePublishNotice(notice.id)}
                                            className="h-8 px-3 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors flex items-center gap-1.5"
                                        >
                                            <Send className="w-3 h-3" /> Publish
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleViewStats(notice)}
                                        className="h-8 px-3 text-xs font-medium text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <BarChart2 className="w-3 h-3" /> Stats
                                    </button>
                                    <button
                                        onClick={() => handleEditNotice(notice)}
                                        className="h-8 px-3 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <Edit className="w-3 h-3" /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteNotice(notice.id)}
                                        className="h-8 px-3 text-xs font-medium text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Notice Creation Modal */}
            {isModalOpen && (
                <NoticeCreationModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedNotice(null);
                    }}
                    onSuccess={() => {
                        fetchNotices();
                        setIsModalOpen(false);
                        setSelectedNotice(null);
                    }}
                    notice={selectedNotice}
                    phase={activeTab}
                />
            )}

            {/* Stats Modal */}
            {statsModalOpen && statsNotice && (
                <NoticeStatsModal
                    isOpen={statsModalOpen}
                    onClose={() => {
                        setStatsModalOpen(false);
                        setStatsNotice(null);
                    }}
                    notice={statsNotice}
                />
            )}

            {/* Attachments List Modal */}
            {viewingNoticeAttachments && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-semibold text-gray-800">
                                Attachments ({viewingNoticeAttachments.attachments.length})
                            </h3>
                            <button
                                onClick={() => setViewingNoticeAttachments(null)}
                                className="p-1 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-2 max-h-[60vh] overflow-y-auto">
                            {viewingNoticeAttachments.attachments.map((file, index) => (
                                <button
                                    key={index}
                                    onClick={() => setPreviewFile(file)}
                                    className="w-full text-left flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg group transition-colors"
                                >
                                    <div className="p-2 bg-gray-100 rounded-lg text-gray-500">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 truncate text-sm">
                                            {file.file_name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Click to preview
                                        </p>
                                    </div>
                                    <Eye className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* File Viewer */}
            <FileViewer
                file={previewFile}
                isOpen={!!previewFile}
                onClose={() => setPreviewFile(null)}
            />

            {/* AI Notice Generator */}
            <AINoticeGenerator
                isOpen={isAIGeneratorOpen}
                onClose={() => {
                    setIsAIGeneratorOpen(false);
                    setAiEditNotice(null);
                }}
                onSuccess={() => {
                    setIsAIGeneratorOpen(false);
                    setAiEditNotice(null);
                    fetchNotices();
                }}
                phase={activeTab}
                editNotice={aiEditNotice}
                initialMessages={aiEditNotice?.ai_chat_history || null}
            />

            {/* Page Level Analytics Drawer */}
            <NoticesAnalyticsDrawer
                isOpen={isPageAnalyticsOpen}
                onClose={() => setIsPageAnalyticsOpen(false)}
            />
        </div>
    );
};

export default AdminNoticesPage;

